import type { Aabb, InteriorTemplate, StoreThemeId } from "@townsy/contracts";
import { interiorWallColliders } from "./collision";

/**
 * Store interior themes (founder feedback round 3: "the inside of all the stores are all the exact
 * same and it is pretty boring"; ADR-0006). Every commercial property still has ONE shared interior
 * room (`store_room`, bounds x -8..8, z -6..6, door on the south wall at z = -6, brand sign and the
 * owner's TV board on the north wall). A theme decides the floor, the walls and the fixtures inside;
 * the renderer tints "brand"/"accent" parts with the business's colors, so two bakeries still differ.
 *
 * Shared by the server (colliders: storeInteriorColliders) and the renderer (models per fixture kind).
 * Positions are interior-local metres (x east, z north); rotation is a yaw in quarter turns (0 = the
 * fixture's front faces +z ... see STORE_FIXTURE_KINDS for each model's "front").
 */

export type StoreFloor = "wood_light" | "wood_dark" | "tile_white" | "checker" | "concrete" | "rubber" | "carpet" | "terrazzo";
export type StoreWall = "cream" | "white" | "sage" | "sky" | "blush" | "sand" | "slate" | "brick" | "charcoal";

/**
 * The visual vocabulary for store fixtures. `footprint` is the default collider (width along the
 * model's local x, depth along local z, before rotation); null = flat or wall-mounted, never solid.
 * The renderer owns the models; the world owns where they stand.
 */
export const STORE_FIXTURE_KINDS = {
  counter: { footprint: { width: 3.2, depth: 1.0 }, note: "sales counter with a register; brand-colored front panel" },
  shelf_tall: { footprint: { width: 2.4, depth: 0.6 }, note: "tall wall shelving stocked with generic boxed goods (brand-tinted uprights)" },
  display_table: { footprint: { width: 1.8, depth: 1.0 }, note: "low table with neatly stacked products" },
  plant_pot: { footprint: { width: 0.7, depth: 0.7 }, note: "large potted plant" },
  display_case: { footprint: { width: 2.0, depth: 0.8 }, note: "glass display case; contents drawn per theme (pastries, phones, jewellery)" },
  bread_rack: { footprint: { width: 1.8, depth: 0.6 }, note: "wooden rack of bread loaves and baguettes" },
  oven: { footprint: { width: 1.6, depth: 0.9 }, note: "stainless deck oven against a wall" },
  bistro_set: { footprint: { width: 1.6, depth: 1.6 }, note: "small round table with two chairs" },
  espresso_bar: { footprint: { width: 3.0, depth: 0.9 }, note: "coffee counter with espresso machine, grinder and cups" },
  lounge_sofa: { footprint: { width: 2.2, depth: 0.9 }, note: "two-seat lounge sofa with cushions" },
  treadmill: { footprint: { width: 0.9, depth: 2.0 }, note: "treadmill with a console, front = console end" },
  exercise_bike: { footprint: { width: 0.6, depth: 1.3 }, note: "stationary bike" },
  weight_rack: { footprint: { width: 2.0, depth: 0.7 }, note: "rack of dumbbells" },
  bench_press: { footprint: { width: 1.2, depth: 2.0 }, note: "bench with a barbell rack" },
  yoga_mat: { footprint: null, note: "flat exercise mat (walkable)" },
  bookshelf_tall: { footprint: { width: 2.2, depth: 0.5 }, note: "tall bookshelf full of colored spines" },
  book_table: { footprint: { width: 1.6, depth: 1.0 }, note: "table with face-up books and small stacks" },
  reading_chair: { footprint: { width: 0.9, depth: 0.9 }, note: "armchair with a small side lamp" },
  reception_desk: { footprint: { width: 3.0, depth: 1.0 }, note: "reception desk with a monitor and the brand mark on the front" },
  office_desk: { footprint: { width: 1.8, depth: 0.9 }, note: "desk with monitor and an office chair behind it" },
  waiting_chairs: { footprint: { width: 2.4, depth: 0.7 }, note: "row of three waiting-room chairs" },
  filing_cabinet: { footprint: { width: 0.6, depth: 0.7 }, note: "metal filing cabinet" },
  conference_table: { footprint: { width: 3.0, depth: 1.6 }, note: "meeting table with chairs around it" },
  flower_stand: { footprint: { width: 1.8, depth: 0.9 }, note: "tiered stand of buckets with bright bouquets" },
  flower_cooler: { footprint: { width: 1.8, depth: 0.8 }, note: "glass-door flower fridge against a wall" },
  plant_table: { footprint: { width: 1.6, depth: 0.9 }, note: "table of small potted plants and succulents" },
  globe_stand: { footprint: { width: 0.8, depth: 0.8 }, note: "large floor globe on a wooden stand" },
  brochure_rack: { footprint: { width: 1.0, depth: 0.5 }, note: "rack of colorful travel brochures" },
  suitcase_display: { footprint: { width: 1.4, depth: 0.8 }, note: "stack of vintage suitcases on a low plinth" },
  travel_desk: { footprint: { width: 1.8, depth: 1.6 }, note: "agent desk with two customer chairs in front" },
  pet_food_shelf: { footprint: { width: 2.4, depth: 0.6 }, note: "shelf of pet food bags and cans" },
  aquarium_tank: { footprint: { width: 2.2, depth: 0.6 }, note: "row of lit fish tanks on a cabinet" },
  cat_tree: { footprint: { width: 0.9, depth: 0.9 }, note: "carpeted cat tree with platforms" },
  pet_bed_display: { footprint: { width: 1.6, depth: 1.0 }, note: "low plinth with round pet beds" },
  workbench: { footprint: { width: 2.2, depth: 0.9 }, note: "repair bench with lamp, tools and an open laptop" },
  tv_display: { footprint: { width: 1.8, depth: 0.6 }, note: "large TV on a low media stand" },
  drafting_table: { footprint: { width: 1.6, depth: 1.1 }, note: "tilted drafting table with a stool" },
  easel: { footprint: { width: 0.8, depth: 0.8 }, note: "painting easel with a canvas" },
  monitor_desk: { footprint: { width: 2.0, depth: 0.9 }, note: "desk with two large monitors and a chair" },
  clothing_rack: { footprint: { width: 1.8, depth: 0.6 }, note: "rail with hanging clothes in a few colors" },
  mannequin: { footprint: { width: 0.6, depth: 0.6 }, note: "dressed mannequin on a round base" },
  fitting_room: { footprint: { width: 1.6, depth: 1.4 }, note: "fitting booth with a curtain" },
  mirror_stand: { footprint: { width: 0.9, depth: 0.5 }, note: "free-standing tall mirror" },
  rug: { footprint: null, note: "flat rug (walkable)" },
  wall_menu: { footprint: null, note: "menu/price board mounted on a wall" },
  wall_poster: { footprint: null, note: "framed poster on a wall (travel, fitness, art...)" },
  wall_mirror: { footprint: null, note: "wide mirror mounted on a wall" },
  // ---- Round 4 (ADR-0008): Tech office ----
  desk_pair: {
    footprint: { width: 1.6, depth: 2.2 },
    note: "two desks pushed together face to face (each 1.6 x 0.7 m, top 0.75 m) with a large monitor on each (top 1.25 m) and an office chair tucked in on each long side (0.4 m); front = local +z",
  },
  server_rack: { footprint: { width: 1.4, depth: 0.9 }, note: "two server racks side by side behind smoked glass doors with small blinking status lights (2.1 m); back to the wall" },
  meeting_pod: {
    footprint: { width: 2.4, depth: 2.0 },
    note: "glass meeting booth (2.3 m): back and side panels, roof panel, open front at local +z, a small table with two seats inside; brand-tinted frame",
  },
  beanbag: { footprint: { width: 0.9, depth: 0.9 }, note: "big round beanbag seat (0.7 m), brand or accent colour" },
  wall_whiteboard: { footprint: null, note: "whiteboard on a wall (2.4 x 1.2 m board, top 2.2 m) with diagrams and sticky notes" },
  // ---- Round 4: Lounge (no gambling equipment) ----
  velvet_booth: {
    footprint: { width: 2.4, depth: 1.4 },
    note: "velvet booth: a tufted bench seat with a tall back (1.3 m) along local -z, a small round table in front; brand-tinted upholstery; back to the wall",
  },
  bar_counter: {
    footprint: { width: 3.0, depth: 1.2 },
    note: "bar counter (top 1.1 m, 3.0 x 0.6 m at local -z) with four bar stools on its front side (local +z, seats 0.75 m); stool tops stay under 1.15 m",
  },
  back_bar: { footprint: { width: 2.8, depth: 0.5 }, note: "back-bar shelving (2.2 m) with glassware, drink bottles and a soft backlight; back to the wall" },
  cocktail_table: { footprint: { width: 0.8, depth: 0.8 }, note: "tall round cocktail table (1.1 m) on a pedestal, no chairs" },
  low_table_set: { footprint: { width: 2.0, depth: 1.2 }, note: "low coffee table (0.8 x 0.6 m, 0.45 m) between two velvet armchairs (backs 0.85 m) facing each other along local x" },
  // ---- Round 4: Gallery ----
  sculpture_plinth: { footprint: { width: 0.8, depth: 0.8 }, note: "white plinth (1.0 m) carrying an abstract sculpture (to 1.9 m), accent-tinted" },
  gallery_bench: { footprint: { width: 1.8, depth: 0.5 }, note: "low upholstered gallery bench (0.45 m) without a back, seats on both long sides" },
  art_partition: { footprint: { width: 2.6, depth: 0.3 }, note: "free-standing white partition wall (2.4 m) on low feet, a framed artwork on each face" },
  wall_art: { footprint: null, note: "large framed artwork on a wall (1.6 x 1.1 m, top 2.4 m) with a small picture light above it" },
} as const satisfies Record<string, { footprint: { width: number; depth: number } | null; note: string }>;

export type StoreFixtureKind = keyof typeof STORE_FIXTURE_KINDS;

export interface StoreFixture {
  kind: StoreFixtureKind;
  position: { x: number; z: number };
  /** Yaw in radians, a multiple of PI/2. */
  rotation: number;
  /** Optional per-fixture override of the kind's default footprint. */
  footprint?: { width: number; depth: number } | null;
}

export interface StoreTheme {
  id: StoreThemeId;
  /** Owner-facing name and one-line description (theme picker). */
  label: string;
  description: string;
  floor: StoreFloor;
  wall: StoreWall;
  fixtures: readonly StoreFixture[];
}

/** Owner-facing label, description, floor and wall of each theme (the layouts are STORE_THEMES below). */
export const STORE_THEME_META: Record<StoreThemeId, Omit<StoreTheme, "id" | "fixtures">> = {
  general: { label: "General store", description: "Counter and stocked shelves.", floor: "wood_light", wall: "cream" },
  bakery: { label: "Bakery", description: "Pastry cases, bread racks, an oven and a couple of café tables.", floor: "checker", wall: "cream" },
  cafe: { label: "Café", description: "Espresso bar, bistro tables and a cosy sofa corner.", floor: "wood_dark", wall: "sand" },
  fitness: { label: "Gym", description: "Treadmills, bikes, weights and a mirror wall.", floor: "rubber", wall: "charcoal" },
  bookstore: { label: "Bookstore", description: "Tall bookshelves, book tables and reading chairs.", floor: "wood_dark", wall: "sage" },
  office: { label: "Office", description: "Reception desk, waiting chairs, desks and a meeting table.", floor: "carpet", wall: "white" },
  florist: { label: "Florist", description: "Flower stands, a flower cooler and tables of plants.", floor: "terrazzo", wall: "blush" },
  travel: { label: "Travel agency", description: "Agent desks, a big globe, brochures and posters.", floor: "tile_white", wall: "sky" },
  pet_shop: { label: "Pet shop", description: "Pet food aisles, aquariums, a cat tree and pet beds.", floor: "tile_white", wall: "sage" },
  tech_repair: { label: "Tech repair", description: "Repair benches, phone cases and screens.", floor: "concrete", wall: "slate" },
  studio: { label: "Design studio", description: "Drafting tables, easels and big monitors.", floor: "concrete", wall: "white" },
  boutique: { label: "Boutique", description: "Clothing racks, mannequins, mirrors and a fitting room.", floor: "terrazzo", wall: "blush" },
  tech_office: { label: "Tech office", description: "Open desks with big screens, a whiteboard wall, a server rack and a meeting pod.", floor: "concrete", wall: "white" },
  lounge: { label: "Lounge", description: "Velvet booths, a bar counter, low tables and soft lighting.", floor: "wood_dark", wall: "charcoal" },
  gallery: { label: "Gallery", description: "Plinths, framed art on every wall and gallery spotlights.", floor: "terrazzo", wall: "white" },
};

/** Collider for one fixture (null when it is walk-over or wall mounted). */
export function storeFixtureCollider(f: StoreFixture): Aabb | null {
  const fp = f.footprint === undefined ? STORE_FIXTURE_KINDS[f.kind].footprint : f.footprint;
  if (!fp) return null;
  const quarter = Math.round(f.rotation / (Math.PI / 2));
  const swap = Math.abs(quarter) % 2 === 1;
  const hw = (swap ? fp.depth : fp.width) / 2;
  const hd = (swap ? fp.width : fp.depth) / 2;
  return { minX: f.position.x - hw, maxX: f.position.x + hw, minZ: f.position.z - hd, maxZ: f.position.z + hd };
}

// ---------------------------------------------------------------------------------------------
// Layout rules and the 12 layouts (world agent, round 3)
// ---------------------------------------------------------------------------------------------

/**
 * Per-kind layout facts the rules (and the renderer) rely on:
 *  - `height`: the TOP of the model above the floor (metres). Models must not be taller: the area
 *    under the brand sign and the owner TV only takes fixtures up to 1.2 m. Wall-mounted kinds give
 *    the top of the hung item.
 *  - `mount`: "floor" (stands on the floor; solid when it has a footprint), "flat" (rug, yoga mat:
 *    walkable, drawn a few cm high) or "wall" (hung on a wall; position is 0.05 m off the wall plane).
 *  - `wallBacked`: the model has a back meant to stand against a wall (shelving, ovens, benches...).
 *    Only these may touch a wall; others keep >= 0.1 m from every wall. A wall-backed fixture that
 *    touches a wall turns its back (local -z) to that wall.
 *  - `size`: the visual footprint (width along local x, depth along local z). For floor kinds it is
 *    the collider footprint; for flat and wall kinds it is what the renderer should draw.
 */
export interface StoreFixtureSpec {
  height: number;
  mount: "floor" | "flat" | "wall";
  wallBacked: boolean;
  size: { width: number; depth: number };
}

const FLAT_OR_WALL_SIZE: Partial<Record<StoreFixtureKind, { width: number; depth: number }>> = {
  yoga_mat: { width: 0.8, depth: 1.9 },
  rug: { width: 3.0, depth: 2.0 },
  wall_menu: { width: 1.8, depth: 0.05 },
  wall_poster: { width: 1.0, depth: 0.05 },
  wall_mirror: { width: 2.4, depth: 0.05 },
  wall_whiteboard: { width: 2.4, depth: 0.05 },
  wall_art: { width: 1.6, depth: 0.06 },
};

const HEIGHTS: Record<StoreFixtureKind, number> = {
  counter: 1.1,
  shelf_tall: 2.2,
  display_table: 0.85,
  plant_pot: 1.5,
  display_case: 1.15,
  bread_rack: 1.8,
  oven: 1.7,
  bistro_set: 0.95,
  espresso_bar: 1.5,
  lounge_sofa: 0.9,
  treadmill: 1.45,
  exercise_bike: 1.3,
  weight_rack: 1.1,
  bench_press: 1.5,
  yoga_mat: 0.025,
  bookshelf_tall: 2.2,
  book_table: 0.9,
  reading_chair: 1.5,
  reception_desk: 1.2,
  office_desk: 1.3,
  waiting_chairs: 0.95,
  filing_cabinet: 1.3,
  conference_table: 1.0,
  flower_stand: 1.4,
  flower_cooler: 2.0,
  plant_table: 1.1,
  globe_stand: 1.3,
  brochure_rack: 1.6,
  suitcase_display: 1.2,
  travel_desk: 1.3,
  pet_food_shelf: 1.8,
  aquarium_tank: 1.6,
  cat_tree: 1.8,
  pet_bed_display: 0.6,
  workbench: 1.5,
  tv_display: 1.6,
  drafting_table: 1.3,
  easel: 1.8,
  monitor_desk: 1.4,
  clothing_rack: 1.7,
  mannequin: 1.85,
  fitting_room: 2.2,
  mirror_stand: 1.9,
  rug: 0.025,
  wall_menu: 2.7,
  wall_poster: 2.3,
  wall_mirror: 2.2,
  desk_pair: 1.25,
  server_rack: 2.1,
  meeting_pod: 2.3,
  beanbag: 0.7,
  wall_whiteboard: 2.2,
  velvet_booth: 1.3,
  bar_counter: 1.15,
  back_bar: 2.2,
  cocktail_table: 1.1,
  low_table_set: 0.85,
  sculpture_plinth: 1.9,
  gallery_bench: 0.45,
  art_partition: 2.4,
  wall_art: 2.4,
};

const WALL_BACKED = new Set<StoreFixtureKind>([
  "shelf_tall",
  "display_case",
  "bread_rack",
  "oven",
  "espresso_bar",
  "lounge_sofa",
  "weight_rack",
  "bookshelf_tall",
  "office_desk",
  "waiting_chairs",
  "filing_cabinet",
  "flower_stand",
  "flower_cooler",
  "brochure_rack",
  "pet_food_shelf",
  "aquarium_tank",
  "workbench",
  "tv_display",
  "monitor_desk",
  "clothing_rack",
  "fitting_room",
  "wall_menu",
  "wall_poster",
  "wall_mirror",
  "server_rack",
  "meeting_pod",
  "wall_whiteboard",
  "velvet_booth",
  "back_bar",
  "wall_art",
]);

function specFor(kind: StoreFixtureKind): StoreFixtureSpec {
  const fp = STORE_FIXTURE_KINDS[kind].footprint;
  const mount: StoreFixtureSpec["mount"] = fp ? "floor" : kind.startsWith("wall_") ? "wall" : "flat";
  return { height: HEIGHTS[kind], mount, wallBacked: WALL_BACKED.has(kind), size: fp ?? FLAT_OR_WALL_SIZE[kind] ?? { width: 1, depth: 1 } };
}

export const STORE_FIXTURE_SPECS: Record<StoreFixtureKind, StoreFixtureSpec> = Object.fromEntries(
  (Object.keys(STORE_FIXTURE_KINDS) as StoreFixtureKind[]).map((k) => [k, specFor(k)]),
) as Record<StoreFixtureKind, StoreFixtureSpec>;

/** The store_room floor (interior-local metres) the layouts are designed for. */
export const STORE_ROOM_BOUNDS: Aabb = { minX: -8, minZ: -6, maxX: 8, maxZ: 6 };

/**
 * Layout zones of store_room (tests enforce them for every theme):
 *  - signArea: under the brand sign (x -3.5..3.5) and the owner TV (x -7.2..-4) on the north wall,
 *    z > 4.6: no fixture taller than `signAreaMaxHeight` and no wall-mounted item;
 *  - doorway: from the exit door (south wall, |x| <= 1.2) to past the spawn (0, -3.6), with the actor
 *    radius on both sides: no solid fixture;
 *  - exitMat: the exit mat in front of the door: no rug or yoga mat on it.
 */
export const STORE_ROOM_ZONES: {
  signArea: readonly Aabb[];
  signAreaMaxHeight: number;
  doorway: Aabb;
  exitMat: Aabb;
} = {
  signArea: [
    { minX: -7.2, minZ: 4.6, maxX: -4, maxZ: 6 },
    { minX: -3.5, minZ: 4.6, maxX: 3.5, maxZ: 6 },
  ],
  signAreaMaxHeight: 1.2,
  doorway: { minX: -1.6, minZ: -6, maxX: 1.6, maxZ: -3.2 },
  exitMat: { minX: -1.1, minZ: -5.7, maxX: 1.1, maxZ: -4.3 },
};

/** Rotation (yaw) that turns a fixture's FRONT to face this way. */
const FACE = { north: 0, east: Math.PI / 2, south: Math.PI, west: -Math.PI / 2 } as const;
type WallName = keyof typeof FACE;
/** A fixture standing with its back against a wall faces away from it. */
const AWAY: Record<WallName, number> = { north: FACE.south, south: FACE.north, west: FACE.east, east: FACE.west };

/** Free-standing fixture centred at (x, z), front facing `face`. */
function at(kind: StoreFixtureKind, x: number, z: number, face: WallName = "south"): StoreFixture {
  return { kind, position: { x, z }, rotation: FACE[face] };
}

/** Flat fixture (rug, yoga mat) centred at (x, z). */
function flat(kind: "rug" | "yoga_mat", x: number, z: number): StoreFixture {
  return { kind, position: { x, z }, rotation: 0 };
}

/**
 * Fixture standing with its back against a wall, centred at `along` (x for north/south walls, z for
 * east/west walls) and touching it; wall-mounted kinds hang 0.05 m off the wall plane.
 */
function wall(kind: StoreFixtureKind, side: WallName, along: number): StoreFixture {
  const R = STORE_ROOM_BOUNDS;
  const spec = STORE_FIXTURE_SPECS[kind];
  const half = spec.mount === "floor" ? spec.size.depth / 2 : 0.05;
  const rotation = AWAY[side];
  switch (side) {
    case "north":
      return { kind, position: { x: along, z: R.maxZ - half }, rotation };
    case "south":
      return { kind, position: { x: along, z: R.minZ + half }, rotation };
    case "west":
      return { kind, position: { x: R.minX + half, z: along }, rotation };
    case "east":
      return { kind, position: { x: R.maxX - half, z: along }, rotation };
  }
}

/** A free-standing fixture in a corner, 0.1 m off both walls, facing into the room. */
function corner(kind: StoreFixtureKind, ns: "north" | "south", ew: "east" | "west"): StoreFixture {
  const R = STORE_ROOM_BOUNDS;
  const fp = STORE_FIXTURE_SPECS[kind].size;
  const gap = 0.1;
  const x = ew === "west" ? R.minX + gap + fp.width / 2 : R.maxX - gap - fp.width / 2;
  const z = ns === "south" ? R.minZ + gap + fp.depth / 2 : R.maxZ - gap - fp.depth / 2;
  return { kind, position: { x, z }, rotation: FACE[ns === "north" ? "south" : "north"] };
}

function theme(id: StoreThemeId, fixtures: StoreFixture[]): StoreTheme {
  return { id, ...STORE_THEME_META[id], fixtures };
}

/**
 * The 15 layouts (12 from round 3, three modern presets from round 4). Every one keeps the doorway and spawn clear, keeps tall things away from the brand
 * sign and the TV, leaves every walkable spot reachable from the spawn and >= 70% of the floor
 * walkable (storeThemes.test.ts). "general" is the round-2 room (a counter in front of the sign and
 * shelving along both side walls) with a few extras.
 */
export const STORE_THEMES: Record<StoreThemeId, StoreTheme> = {
  general: theme("general", [
    at("counter", 0, 2.8),
    wall("shelf_tall", "west", 1.6),
    wall("shelf_tall", "west", -0.8),
    wall("shelf_tall", "east", 1.6),
    wall("shelf_tall", "east", -0.8),
    at("display_table", -3.6, -0.6),
    at("display_table", 3.6, -0.6),
    corner("plant_pot", "north", "west"),
    corner("plant_pot", "north", "east"),
    wall("wall_poster", "south", -4.6),
  ]),
  bakery: theme("bakery", [
    at("display_case", -1.0, 2.6),
    at("display_case", 1.0, 2.6),
    wall("oven", "north", 5.6),
    wall("bread_rack", "east", 0.5),
    wall("bread_rack", "east", -1.3),
    at("bistro_set", -6.9, -4.9),
    at("bistro_set", -6.9, -1.4),
    at("display_table", 6.6, -3.6, "north"),
    wall("wall_menu", "west", 2.6),
    corner("plant_pot", "north", "west"),
  ]),
  cafe: theme("cafe", [
    wall("espresso_bar", "north", 5.4),
    at("counter", 5.4, 3.2),
    at("bistro_set", -2.4, 1.6),
    at("bistro_set", 1.6, 0.2),
    at("bistro_set", 6.9, -4.9),
    wall("lounge_sofa", "west", -2.6),
    wall("lounge_sofa", "south", -5.5),
    corner("plant_pot", "south", "west"),
    corner("plant_pot", "north", "west"),
    flat("rug", -5.4, -3.6),
    wall("wall_poster", "west", 1.8),
  ]),
  fitness: theme("fitness", [
    at("treadmill", 4.3, 4.85, "north"),
    at("treadmill", 5.5, 4.85, "north"),
    at("treadmill", 6.7, 4.85, "north"),
    at("exercise_bike", -6.9, 2.0, "east"),
    at("exercise_bike", -6.9, 3.4, "east"),
    wall("weight_rack", "west", -1.0),
    at("bench_press", -4.6, -1.0, "east"),
    flat("yoga_mat", 3.0, -1.2),
    flat("yoga_mat", 4.2, -1.2),
    wall("tv_display", "south", 5.0),
    wall("wall_mirror", "east", -1.6),
    wall("wall_mirror", "east", 1.2),
    wall("wall_poster", "south", -4.6),
  ]),
  bookstore: theme("bookstore", [
    wall("bookshelf_tall", "west", 4.9),
    wall("bookshelf_tall", "west", 2.7),
    wall("bookshelf_tall", "west", 0.5),
    wall("bookshelf_tall", "east", 4.9),
    wall("bookshelf_tall", "east", 2.7),
    wall("bookshelf_tall", "east", 0.5),
    wall("bookshelf_tall", "north", 5.4),
    at("book_table", -2.8, 0.6),
    at("book_table", 2.8, 0.6),
    at("reading_chair", -4.8, -3.4, "east"),
    at("reading_chair", 4.8, -3.4, "west"),
    flat("rug", 0, -1.4),
    corner("plant_pot", "south", "east"),
    wall("wall_poster", "south", -4.6),
  ]),
  office: theme("office", [
    at("reception_desk", 0, 2.4),
    wall("waiting_chairs", "west", -2.0),
    wall("waiting_chairs", "south", -4.5),
    corner("plant_pot", "south", "west"),
    wall("office_desk", "east", -0.4),
    wall("office_desk", "east", 1.6),
    at("conference_table", 4.0, -3.4),
    wall("filing_cabinet", "north", 4.9),
    wall("filing_cabinet", "north", 5.6),
    corner("plant_pot", "north", "east"),
    wall("wall_poster", "south", 4.0),
  ]),
  florist: theme("florist", [
    wall("flower_cooler", "north", 5.2),
    at("counter", 0, 2.6),
    wall("flower_stand", "west", 1.0),
    wall("flower_stand", "west", -1.4),
    wall("flower_stand", "east", -0.6),
    at("plant_table", -3.4, -1.6),
    at("plant_table", 3.4, -1.6),
    corner("plant_pot", "south", "west"),
    corner("plant_pot", "south", "east"),
    wall("wall_poster", "west", 4.0),
  ]),
  travel: theme("travel", [
    at("travel_desk", -3.0, 2.4),
    at("travel_desk", 3.0, 2.4),
    at("globe_stand", 0, -0.6),
    wall("brochure_rack", "west", -1.0),
    wall("brochure_rack", "west", 0.4),
    wall("brochure_rack", "east", -1.0),
    at("suitcase_display", -5.4, -3.6, "north"),
    wall("waiting_chairs", "south", 5.0),
    corner("plant_pot", "north", "east"),
    wall("wall_poster", "west", 3.0),
    wall("wall_poster", "east", 3.0),
    wall("wall_poster", "south", -4.6),
  ]),
  pet_shop: theme("pet_shop", [
    wall("pet_food_shelf", "west", 0),
    wall("pet_food_shelf", "west", 2.4),
    at("pet_food_shelf", -3.6, 0.6, "east"),
    wall("aquarium_tank", "east", 0),
    wall("aquarium_tank", "east", 2.2),
    wall("aquarium_tank", "north", 5.6),
    at("cat_tree", 5.4, -3.2),
    at("pet_bed_display", 2.6, -2.6, "north"),
    at("counter", 0, 2.8),
    wall("wall_poster", "south", -4.6),
  ]),
  tech_repair: theme("tech_repair", [
    wall("workbench", "east", -0.45),
    wall("workbench", "east", 1.8),
    at("counter", 0, 2.6),
    wall("display_case", "west", 0.5),
    wall("display_case", "west", 2.5),
    wall("tv_display", "north", 5.0),
    wall("tv_display", "south", -4.6),
    at("display_table", -3.4, -1.6, "north"),
    wall("shelf_tall", "south", 5.0),
    wall("wall_poster", "west", -3.0),
  ]),
  studio: theme("studio", [
    at("drafting_table", -4.4, 1.8),
    at("drafting_table", -4.4, -1.0),
    wall("monitor_desk", "east", -0.2),
    wall("monitor_desk", "east", 2.0),
    at("easel", -6.4, -3.6, "north"),
    at("easel", -4.8, -3.6, "north"),
    at("easel", -3.2, -3.6, "north"),
    corner("plant_pot", "north", "east"),
    wall("lounge_sofa", "north", 5.0),
    flat("rug", 4.0, -2.8),
    wall("wall_poster", "west", 3.6),
    wall("wall_poster", "south", 4.6),
  ]),
  boutique: theme("boutique", [
    wall("fitting_room", "north", 7.2),
    at("mirror_stand", 5.2, 5.65),
    wall("clothing_rack", "west", 1.2),
    wall("clothing_rack", "west", -1.2),
    at("clothing_rack", -3.2, 0.4),
    at("clothing_rack", 3.2, 0.4),
    at("mannequin", -2.8, 3.2),
    at("mannequin", 2.8, 3.2),
    at("mannequin", 5.6, -2.8),
    at("counter", 0, 2.6),
    wall("wall_mirror", "east", -1.4),
    flat("rug", 0, -1.2),
  ]),
  // Round 4 (ADR-0008): three modern presets.
  tech_office: theme("tech_office", [
    wall("meeting_pod", "north", 6.8),
    wall("server_rack", "east", 1.6),
    at("desk_pair", -3.8, 0.8),
    at("desk_pair", 0.6, 0.8),
    wall("wall_whiteboard", "west", 1.0),
    flat("rug", 5.6, -3.4),
    at("beanbag", 4.8, -3.6),
    at("beanbag", 6.4, -2.8),
    wall("lounge_sofa", "south", -5.0),
    corner("plant_pot", "north", "west"),
  ]),
  lounge: theme("lounge", [
    wall("back_bar", "north", 5.6),
    at("bar_counter", 5.6, 3.4),
    wall("velvet_booth", "west", 2.6),
    wall("velvet_booth", "west", -0.6),
    at("low_table_set", -2.8, -1.6),
    flat("rug", -2.8, -1.6),
    at("cocktail_table", 2.4, -0.4),
    at("cocktail_table", 4.4, -2.4),
    corner("plant_pot", "south", "east"),
    wall("wall_mirror", "east", -1.2),
  ]),
  gallery: theme("gallery", [
    at("reception_desk", 0, 3.0),
    at("art_partition", -3.6, 0.4, "east"),
    at("art_partition", 3.6, 0.4, "west"),
    at("sculpture_plinth", 0, -0.6),
    at("sculpture_plinth", -5.6, -3.2),
    at("sculpture_plinth", 5.6, -3.2),
    at("gallery_bench", 0, 0.9),
    wall("wall_art", "west", 2.4),
    wall("wall_art", "west", -1.4),
    wall("wall_art", "east", 2.4),
    wall("wall_art", "east", -1.4),
    wall("wall_art", "south", -4.8),
    wall("wall_art", "south", 4.8),
    corner("plant_pot", "north", "west"),
  ]),
};

/** The theme for an id (null, undefined or unknown ids fall back to "general"). */
export function storeTheme(themeId: StoreThemeId | string | null | undefined): StoreTheme {
  return (themeId ? (STORE_THEMES as Record<string, StoreTheme | undefined>)[themeId] : undefined) ?? STORE_THEMES.general;
}

/** Collider boxes of a theme's solid fixtures (interior-local metres). */
export function storeThemeFixtureColliders(themeId: StoreThemeId | string | null | undefined): Aabb[] {
  const out: Aabb[] = [];
  for (const f of storeTheme(themeId).fixtures) {
    const box = storeFixtureCollider(f);
    if (box) out.push(box);
  }
  return out;
}

/**
 * Colliders of a themed store interior: the theme's fixture colliders plus the wall ring of
 * `template` (store_room). It REPLACES interiorColliders(store_room) for commercial properties (that
 * template's built-in counter and shelves are the round-2 layout). Unknown themes fall back to
 * "general".
 */
export function storeInteriorColliders(template: Pick<InteriorTemplate, "bounds">, themeId: StoreThemeId | string | null | undefined): Aabb[] {
  return [...storeThemeFixtureColliders(themeId), ...interiorWallColliders(template)];
}
